import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import cx from 'classnames'
import {
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  FloatingPortal,
  autoUpdate,
  shift as shiftMiddleware,
  offset as offsetMiddleware
} from '@floating-ui/react-dom-interactions'

import styles from './Dropdown.module.css'

const DropdownContext = createContext({
  hide: undefined,
  getReferenceProps: undefined
})

export const useDropdownContext = () => useContext(DropdownContext)

export const DropdownItem = (props) => {
  const { children, className, disabled = false, ...rest } = props
  const { hide } = useDropdownContext()

  const handleClick = async () => {
    hide()

    if (!props.onClick) {
      return
    }

    props.onClick()
  }

  return (
    <div
      className={cx(styles.DropdownItem, className, {
        [styles.Disabled]: disabled,
        [styles.Clickable]: Boolean(props.onClick)
      })}
      {...rest}
      onClick={handleClick}
    >
      {children}
    </div>
  )
}

export const Dropdown = (props) => {
  const { className, offset = 5, placement = 'bottom-end', action, dropdown, ...rest } = props

  const [showOptions, setShowOptions] = useState(false)
  const {
    x = '',
    y = '',
    reference,
    floating,
    strategy,
    refs,
    update,
    context
  } = useFloating({
    placement,
    open: showOptions,
    onOpenChange: setShowOptions,
    middleware: [shiftMiddleware(), offsetMiddleware(offset)]
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([useClick(context), useDismiss(context)])

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && showOptions) {
      return autoUpdate(refs.reference.current, refs.floating.current, update)
    }
  }, [refs.reference, refs.floating, update, showOptions])

  const hide = useCallback(() => {
    setShowOptions(false)
  }, [])

  const getRefProps = useCallback(() => {
    return getReferenceProps({ ref: reference })
  }, [getReferenceProps, reference])

  const contextValues = {
    hide,
    getReferenceProps: getRefProps
  }

  const floatingProps = getFloatingProps({
    ref: floating,
    className: styles.DropdownContainer,
    style: {
      position: strategy,
      top: y ?? '',
      left: x ?? ''
    }
  })

  return (
    <DropdownContext.Provider value={contextValues}>
      <div className={cx(styles.Options, className)} {...rest}>
        {action}
        <FloatingPortal>{showOptions && <div {...floatingProps}>{dropdown}</div>}</FloatingPortal>
      </div>
    </DropdownContext.Provider>
  )
}

export default Dropdown
